import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { MessageSquareIcon, UserIcon, XIcon } from "lucide-react";
import { ScrollArea } from "@component/scroll-area";
import { cn } from "@util/tailwind";
import { useUserProfileContext } from "@context/user_profile";
import AiThinkingAnimation from "./ai-thinking-animation";

/** Two-letter initials from a first/last name pair, falling back gracefully. */
function getUserInitials(first?: string | null, last?: string | null): string {
    const f = (first || "").trim();
    const l = (last || "").trim();
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    if (l) return l.slice(0, 2).toUpperCase();
    return "";
}

// Updated interface to match what we send from VideoContainer
interface Message {
    id: string;
    role: string;
    content: string;
    timestamp: Date;
    isComplete?: boolean;
}

interface TranscriptionPanelProps {
    messages?: Message[];
    aiName?: string;
    aiSpeaking?: boolean;
    userSpeaking?: boolean;
    aiThinking?: boolean;
    className?: string;
    onClose?: () => void;
    isVisible?: boolean;
    aiLogoSrc?: string; // AI logo source for transcription
    colorScheme?: 'default' | 'black'; // Color scheme for different interview types
    hasFormAssessment?: boolean;
    hasEligibilityCheck?: boolean;
    tipText?: string;
}

// Interface for typing animation state
interface TypingState {
    displayedContent: string;
    targetContent: string;
    isAnimating: boolean;
    charIndex: number;
    queue: string[];
    lastUpdateTime: number;
}

// AI TTS emotion / direction tags — e.g. "[warm]", "[thoughtful]", "[curious]" —
// steer the synthesized voice but must NEVER appear in the candidate-facing
// transcript. Strip any single-token lowercase tag (optionally with an underscore)
// plus the whitespace that follows it. Kept deliberately narrow so it can't eat
// legitimate bracketed text a candidate might type.
const EMOTION_TAG_RE = /\[[a-z_]+\]\s*/g;
const stripEmotionTags = (text: string): string => {
    if (!text) return text;
    return text.replace(EMOTION_TAG_RE, "").replace(/^[ \t]+/, "");
};

// Helper function to check if message is just YES or NO
const isYesNoMessage = (content: string): boolean => {
    if (!content) return false;

    // Normalize the content: trim whitespace and remove punctuation
    const normalized = content.trim().replace(/[.,!?;:]*/g, '').toUpperCase();

    // Check if it's just YES or NO
    return normalized === 'YES' || normalized === 'NO';
};

export default function TranscriptionPanel({
    messages: rawMessages = [],
    aiName = "ourteam AI",
    aiSpeaking = false,
    userSpeaking = false,
    aiThinking = false,
    className,
    onClose,
    aiLogoSrc,
    colorScheme = 'default',
    hasFormAssessment = false,
    hasEligibilityCheck = false,
    tipText,
}: TranscriptionPanelProps) {
    const [, currentUserInfo] = useUserProfileContext();
    const userInitials = getUserInitials(currentUserInfo?.first_name, currentUserInfo?.last_name);
    // Sanitize every message's text once so the emotion tags never reach the
    // typewriter/display. Dividers pass through untouched.
    const messages = useMemo(
        () => rawMessages.map((m) => (m.role === "divider" ? m : { ...m, content: stripEmotionTags(m.content) })),
        [rawMessages],
    );
    const totalSteps = 1 + (hasFormAssessment ? 1 : 0) + (hasEligibilityCheck ? 1 : 0);
    const showNoteChip = totalSteps > 1;    // Color scheme configuration
    const isBlackScheme = colorScheme === 'black';
    const colorClasses = isBlackScheme ? {
        background: 'bg-white/95 text-gray-900',
        border: 'border-gray-700',
        headerBg: 'bg-white/95',
        headerBorder: 'border-gray-200',
        headerText: 'text-gray-900',
        closeButton: 'text-gray-500 hover:text-gray-700',
        messageAi: 'bg-gray-50 border-gray-100',
        messageUser: 'bg-gray-50 border-gray-200',
        messageText: 'text-gray-700',
        timestampText: 'text-gray-500',
        scrollButton: 'bg-black text-white hover:bg-gray-800',
        cursor: 'bg-black'
    } : {
        background: 'bg-white/95 text-gray-900',
        border: 'border-gray-700',
        headerBg: 'bg-white/95',
        headerBorder: 'border-gray-200',
        headerText: 'text-gray-900',
        closeButton: 'text-gray-500 hover:text-gray-700',
        messageAi: 'bg-blue-50 border-blue-100',
        messageUser: 'bg-gray-50 border-gray-200',
        messageText: 'text-gray-700',
        timestampText: 'text-gray-500',
        scrollButton: 'bg-blue-500 text-white hover:bg-blue-600',
        cursor: 'bg-blue-500'
    };

    // Filter out YES/NO messages, and hide in-flight user messages until they're complete
    // so we run the typewriter animation on the full collected transcript.
    // Dividers (role === "divider") always pass through.
    const filteredMessages = messages.filter((message) => {
        if (message.role === "divider") return true;
        if (isYesNoMessage(message.content)) return false;
        if (message.role !== "ai" && !message.isComplete) return false;
        return true;
    });

    const [localMessages, setLocalMessages] = useState<Message[]>(filteredMessages);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const contentHeightRef = useRef<number>(0);
    const scrollPositionRef = useRef<number>(0);
    const observerRef = useRef<ResizeObserver | null>(null);
    const autoScrollEnabledRef = useRef<boolean>(true);

    // Track typing animation state for each message
    const [typingStates, setTypingStates] = useState<Record<string, TypingState>>({});

    // Animation frame references for cleanup
    const animationFramesRef = useRef<Record<string, number>>({});
    const isAnimatingRef = useRef<Record<string, boolean>>({});

    // Random typing speed helpers for natural effect - adjusted for smoother animation
    const getRandomTypingDelay = () => Math.floor(Math.random() * 15); // 15-25ms - slightly faster
    const getCharDelay = (char: string) => {
        // // Longer pauses after punctuation
        // if (['.', '!', '?'].includes(char)) {
        //     return Math.floor(Math.random() * 30) + 1000; // 30-60ms
        // }
        // if ([',', ';', ':'].includes(char)) {
        //     return Math.floor(Math.random() * 20) + 500; // 20-40ms
        // }
        return getRandomTypingDelay();
    };

    // Process a single character typing animation with improved timing
    const animateTyping = (messageId: string) => {
        if (!isAnimatingRef.current[messageId]) return;

        setTypingStates(prevStates => {
            const state = prevStates[messageId];
            if (!state) return prevStates;

            // If we've reached the end of current content
            if (state.charIndex >= state.targetContent.length) {
                // Process next content in queue if available
                if (state.queue.length > 0) {
                    const newTargetContent = state.targetContent + state.queue[0];
                    const newQueue = state.queue.slice(1);

                    const newState = {
                        ...state,
                        targetContent: newTargetContent,
                        queue: newQueue
                    };

                    // Continue animation with new content
                    scheduleNextChar(messageId, newState);

                    return {
                        ...prevStates,
                        [messageId]: newState
                    };
                }

                // No more content to type
                isAnimatingRef.current[messageId] = false;
                return {
                    ...prevStates,
                    [messageId]: {
                        ...state,
                        isAnimating: false,
                        lastUpdateTime: Date.now()
                    }
                };
            }

            // Still have characters to type
            const nextChar = state.targetContent[state.charIndex];
            const newDisplayedContent = state.displayedContent + nextChar;
            const newCharIndex = state.charIndex + 1;

            // Schedule next character
            const newState = {
                ...state,
                displayedContent: newDisplayedContent,
                charIndex: newCharIndex,
                lastUpdateTime: Date.now()
            };

            scheduleNextChar(messageId, newState);

            // Trigger scroll to bottom for each character typed
            scrollToBottom();

            return {
                ...prevStates,
                [messageId]: newState
            };
        });
    };

    // Enhanced function to schedule the next character with smoother animation
    const scheduleNextChar = useCallback((messageId: string, currentState: TypingState) => {
        // Clear any existing animation for this message
        if (animationFramesRef.current[messageId]) {
            window.clearTimeout(animationFramesRef.current[messageId]);
        }

        // If we've reached the end of the content, finish the animation
        if (currentState.charIndex >= currentState.targetContent.length) {
            setTypingStates(prev => ({
                ...prev,
                [messageId]: {
                    ...currentState,
                    isAnimating: false,
                    displayedContent: currentState.targetContent
                }
            }));
            isAnimatingRef.current[messageId] = false;
            delete animationFramesRef.current[messageId];
            return;
        }

        // Continue animation regardless of completion status
        const nextChar = currentState.targetContent[currentState.charIndex];
        const delay = getCharDelay(nextChar);

        animationFramesRef.current[messageId] = window.setTimeout(() => {
            setTypingStates(prev => {
                const state = prev[messageId];
                if (!state || !state.isAnimating) return prev;

                const newDisplayedContent = state.displayedContent + nextChar;
                const newState = {
                    ...state,
                    displayedContent: newDisplayedContent,
                    charIndex: state.charIndex + 1,
                    lastUpdateTime: Date.now()
                };

                // Continue to next character
                scheduleNextChar(messageId, newState);

                return {
                    ...prev,
                    [messageId]: newState
                };
            });
        }, delay);
    }, []);

    // Clean up any ongoing animation for a message
    const clearAnimationForMessage = (messageId: string) => {
        if (animationFramesRef.current[messageId]) {
            window.clearTimeout(animationFramesRef.current[messageId]);
            delete animationFramesRef.current[messageId];
        }
    };

    // typing animation starter
    const startTypingAnimation = (message: Message) => {
        const existingState = typingStates[message.id];
        const currentTime = Date.now();

        // Dividers are static — no animation needed
        if (message.role === 'divider') return;

        // For user messages, just show content immediately (no animation)
        if (message.role !== 'ai') {
            setTypingStates(prev => ({
                ...prev,
                [message.id]: {
                    displayedContent: message.content,
                    targetContent: message.content,
                    isAnimating: false,
                    charIndex: message.content.length,
                    queue: [],
                    lastUpdateTime: currentTime
                }
            }));
            return;
        }

        // For complete AI messages, show immediately if preferred
        if (message.isComplete) {
            setTypingStates(prev => ({
                ...prev,
                [message.id]: {
                    displayedContent: message.content,
                    targetContent: message.content,
                    isAnimating: false,
                    charIndex: message.content.length,
                    queue: [],
                    lastUpdateTime: currentTime
                }
            }));
            return;
        }

        // For new AI messages (no existing state)
        if (!existingState) {
            const initialState: TypingState = {
                displayedContent: '',
                targetContent: message.content,
                isAnimating: true,
                charIndex: 0,
                queue: [],
                lastUpdateTime: currentTime
            };

            setTypingStates(prev => ({
                ...prev,
                [message.id]: initialState
            }));

            // Start the animation
            isAnimatingRef.current[message.id] = true;
            scheduleNextChar(message.id, initialState);
            return;
        }

        // For existing messages with potentially new content
        const currentContent = existingState.targetContent;
        const newContent = message.content;

        // If content hasn't changed, nothing to update
        if (currentContent === newContent) return;

        // Calculate what content is truly new by checking existing target + queue
        let effectiveCurrentContent = currentContent;

        // Consider all queued content as already "processed"
        existingState.queue.forEach(queuedContent => {
            effectiveCurrentContent += queuedContent;
        });

        // If the new content is already fully contained in what we're processing, skip it
        if (effectiveCurrentContent.includes(newContent)) {
            return;
        }

        // Find the truly new portion (what hasn't been processed yet)
        let addedContent = '';

        // Find the longest common prefix between the new content and effective current content
        let i = 0;
        while (i < Math.min(newContent.length, effectiveCurrentContent.length) &&
            newContent[i] === effectiveCurrentContent[i]) {
            i++;
        }

        // If new content is longer than what we're already processing, extract the new part
        if (newContent.length > i) {
            addedContent = newContent.slice(i);
        }

        // If there's no new content, we're done
        if (!addedContent) return;

        if (existingState.isAnimating) {
            // Already animating, add new content to queue
            setTypingStates(prev => {
                const current = prev[message.id];
                if (!current) return prev;

                return {
                    ...prev,
                    [message.id]: {
                        ...current,
                        queue: [...current.queue, addedContent],
                        lastUpdateTime: currentTime
                    }
                };
            });
        } else {
            // Not currently animating, start fresh animation for new content
            const newState: TypingState = {
                ...existingState,
                targetContent: existingState.displayedContent + addedContent,
                isAnimating: true,
                charIndex: existingState.displayedContent.length,
                queue: [],
                lastUpdateTime: currentTime
            };

            setTypingStates(prev => ({
                ...prev,
                [message.id]: newState
            }));

            // Start the animation
            isAnimatingRef.current[message.id] = true;
            scheduleNextChar(message.id, newState);
        }
    };

    // Add a new effect that handles animation updates on component render
    useEffect(() => {
        // Ensure animations are consistent across renders
        Object.entries(typingStates).forEach(([messageId, state]) => {
            if (state.isAnimating && !isAnimatingRef.current[messageId]) {
                isAnimatingRef.current[messageId] = true;
                scheduleNextChar(messageId, state);
            }
        });
    }, [typingStates]);

    // Effect to initialize the typing animation for the default message on mount
    useEffect(() => {
        // Initialize default message animation on mount
        const defaultMessage = localMessages[0];
        if (defaultMessage && defaultMessage.id === "0" && !isYesNoMessage(defaultMessage.content)) {
            // Set initial typing state for the default message
            setTypingStates({
                "0": {
                    displayedContent: '',
                    targetContent: defaultMessage.content,
                    isAnimating: true,
                    charIndex: 0,
                    queue: [],
                    lastUpdateTime: Date.now()
                }
            });

            // Start animation for default message
            isAnimatingRef.current["0"] = true;
            scheduleNextChar("0", {
                displayedContent: '',
                targetContent: defaultMessage.content,
                isAnimating: true,
                charIndex: 0,
                queue: [],
                lastUpdateTime: Date.now()
            });
        }
    }, []); // Empty dependency array to run only on mount

    // Enhanced cleanup function
    useEffect(() => {
        return () => {
            // Clear all animations
            Object.keys(animationFramesRef.current).forEach(id => {
                window.clearTimeout(animationFramesRef.current[id]);
            });
            isAnimatingRef.current = {};
        };
    }, []);

    // Enhanced scroll to bottom function that's more reliable
    const scrollToBottom = useCallback((force: boolean = false) => {
        if (!scrollAreaRef.current) return;

        // Cancel any pending scroll operations
        if (typeof window !== 'undefined') {
            window.cancelAnimationFrame(scrollToBottomRAF.current);
        }

        // Schedule a new scroll operation using requestAnimationFrame for better performance
        scrollToBottomRAF.current = requestAnimationFrame(() => {
            if (!scrollAreaRef.current) return;

            const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
            if (!scrollContainer) return;

            // Get the scroll container's height and scroll dimensions
            const { scrollHeight, clientHeight, scrollTop } = scrollContainer as HTMLDivElement;

            // Update our tracking refs
            contentHeightRef.current = scrollHeight;
            scrollPositionRef.current = scrollTop;

            // Check if user has scrolled up (not at bottom)
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 30;

            // If user scrolled up and we're not forcing scroll, respect their position
            if (!isAtBottom && !force && !autoScrollEnabledRef.current) {
                return;
            }

            // Re-enable auto-scroll when already at the bottom
            if (isAtBottom) {
                autoScrollEnabledRef.current = true;
            }

            // Perform the scroll with a small delay to ensure content is rendered
            setTimeout(() => {
                if (scrollContainer) {
                    (scrollContainer as HTMLDivElement).scrollTop = scrollHeight;
                    // Double-check that we're at the bottom after a slight delay
                    setTimeout(() => {
                        if (scrollContainer && autoScrollEnabledRef.current) {
                            (scrollContainer as HTMLDivElement).scrollTop =
                                (scrollContainer as HTMLDivElement).scrollHeight;
                        }
                    }, 50);
                }
            }, 10);
        });
    }, []);

    // Update local messages when props change
    useEffect(() => {
        if (messages.length > 0) {
            // Apply YES/NO filtering, and skip in-flight user messages so the
            // typewriter animation runs once on the fully-collected transcript.
            const filteredMessages = messages.filter((message) => {
                if (isYesNoMessage(message.content)) return false;
                if (message.role !== "ai" && !message.isComplete) return false;
                return true;
            });
            setLocalMessages(filteredMessages);

            // Process new messages or content updates
            filteredMessages.forEach((message) => {
                const existingState = typingStates[message.id];

                // If this is a new message or content has changed, start/restart animation
                if (!existingState || existingState.targetContent !== message.content) {
                    // Always start animation from current position, continuing to the end
                    const startIndex = existingState?.displayedContent?.length || 0;

                    setTypingStates(prev => ({
                        ...prev,
                        [message.id]: {
                            displayedContent: existingState?.displayedContent || '',
                            targetContent: message.content,
                            isAnimating: true,
                            charIndex: startIndex,
                            queue: [],
                            lastUpdateTime: Date.now()
                        }
                    }));

                    // Start animation
                    isAnimatingRef.current[message.id] = true;
                    scheduleNextChar(message.id, {
                        displayedContent: existingState?.displayedContent || '',
                        targetContent: message.content,
                        isAnimating: true,
                        charIndex: startIndex,
                        queue: [],
                        lastUpdateTime: Date.now()
                    });
                }
            });

            // Force scroll to bottom with new messages
            scrollToBottom(true);
        }
    }, [messages, scrollToBottom]);

    // Keep track of the animation frame used for scrolling
    const scrollToBottomRAF = useRef<number>(0);

    // Detect manual scrolling to temporarily disable auto-scroll
    const handleScroll = useCallback((event: Event) => {
        const scrollContainer = event.target as HTMLDivElement;
        const { scrollHeight, clientHeight, scrollTop } = scrollContainer;

        // Check if user has scrolled up
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 30;

        // If user scrolled up, disable auto-scroll until they scroll back down
        autoScrollEnabledRef.current = isAtBottom;

        // Update our position tracking
        scrollPositionRef.current = scrollTop;
    }, []);

    // Setup scroll event listeners and resize observer
    useEffect(() => {
        if (!scrollAreaRef.current) return;

        // Get the scroll viewport element
        const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (!scrollContainer) return;

        // Add scroll event listener to detect manual scrolling
        scrollContainer.addEventListener('scroll', handleScroll);

        // Create a ResizeObserver to detect content height changes
        observerRef.current = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const newHeight = entry.contentRect.height;

                // Only trigger scroll if content height increased
                if (newHeight > contentHeightRef.current) {
                    scrollToBottom();
                }

                contentHeightRef.current = newHeight;
            }
        });

        // Observe the scroll content
        const scrollContent = scrollContainer.firstElementChild;
        if (scrollContent) {
            observerRef.current.observe(scrollContent);
        }

        // Initial scroll to bottom
        scrollToBottom(true);

        // Clean up listeners and observers
        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            cancelAnimationFrame(scrollToBottomRAF.current);
        };
    }, [handleScroll, scrollToBottom]);

    // Additional effect to handle typing animation updates scrolling
    useEffect(() => {
        // Scroll down when typing state changes
        const hasActiveTyping = Object.values(typingStates).some(
            state => state.isAnimating
        );

        if (hasActiveTyping) {
            scrollToBottom();
        }
    }, [typingStates, scrollToBottom]);

    // Effect to scroll to bottom when AI thinking state changes
    useEffect(() => {
        if (aiThinking) {
            scrollToBottom(true);
        }
    }, [aiThinking, scrollToBottom]);

    // Scroll to bottom helper function
    const scrollToBottomOld = () => {
        setTimeout(() => {
            if (scrollAreaRef.current) {
                const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
                if (scrollContainer) {
                    scrollContainer.scrollTop = scrollContainer.scrollHeight;
                }
            }
        }, 100);
    };

    // Format time as HH:MM AM/PM
    const formatTime = (date: Date) => {
        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    }; return (
        <div
            className={cn(
                "w-80 md:w-96 hidden sm:flex flex-col h-full",
                colorClasses.background,
                "border-l",
                colorClasses.border,
                className
            )}
        >            {/* Header */}
            <div
                className={cn(
                    "flex items-center justify-between p-4",
                    "border-b",
                    colorClasses.headerBorder
                )}
            >
                <div className="flex items-center gap-2">                    <MessageSquareIcon className={cn(
                    "h-5 w-5",
                    isBlackScheme ? "text-black" : "text-blue-600"
                )} />
                    <h2 className="font-semibold text-lg">
                        Live Transcription
                        {aiSpeaking && (
                            <span className="ml-2 inline-block">
                                <span className={cn(
                                    "h-2 w-2 mb-3 rounded-full animate-pulse inline-block mr-1",
                                    colorClasses.cursor
                                )}></span>
                                {/* <span className="text-sm font-normal text-green-600">AI speaking</span> */}
                            </span>
                        )}
                    </h2>
                </div>                {/* Close button */}
                <button
                    onClick={onClose}
                    className={cn(
                        "p-1 rounded-full transition-colors",
                        colorClasses.closeButton,
                        isBlackScheme ? "hover:bg-gray-800" : "hover:bg-gray-200"
                    )}
                    aria-label="Close transcription panel"
                >
                    <XIcon className="h-5 w-5" />
                </button>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 mx-4 mt-3">
                <span
                    style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#2563EB",
                        background: "#EFF6FF",
                        borderRadius: 4,
                        padding: "2px 6px",
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                        minWidth: 40,
                        textAlign: "center",
                    }}
                >
                    TIP
                </span>
                <span className="text-xs text-gray-600 leading-snug">
                    {tipText ?? "Aim for 30 to 60 seconds per answer."}
                </span>
            </div>
            {showNoteChip && (
                <div className="flex items-center gap-2 flex-shrink-0 mx-4 mt-2">
                    <span
                        style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#2563EB",
                            background: "#EFF6FF",
                            borderRadius: 4,
                            padding: "2px 6px",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                            minWidth: 36,
                            textAlign: "center",
                        }}
                    >
                        NOTE
                    </span>
                    <span className="text-xs text-gray-600 leading-snug">
                        This interview is{" "}
                        <strong style={{ color: "#374151", fontWeight: 600 }}>
                            step 1 of {totalSteps}
                        </strong>
                        . Complete all steps to finish your application.
                    </span>
                </div>
            )}

            {/* Messages */}
            <ScrollArea
                className="flex-1 p-4"
                ref={scrollAreaRef}
            >
                <div
                    className="space-y-6"
                    style={{ marginRight: "10px", marginLeft: "10px" }}
                >
                    {localMessages.map((message) => {
                        if (message.role === "divider") {
                            return (
                                <div key={message.id} className="flex items-center gap-3 py-1">
                                    <div className="flex-1 h-px bg-gray-300" />
                                    <span className="text-xs text-gray-400 font-medium whitespace-nowrap">{message.content}</span>
                                    <div className="flex-1 h-px bg-gray-300" />
                                </div>
                            );
                        }

                        const typingState = typingStates[message.id];
                        let displayContent = typingState?.displayedContent || '';
                        // remove the last character if it's a newline
                        const lastChar = displayContent[displayContent.length - 1];
                        displayContent = lastChar === '\n' ? displayContent.slice(0, -1) : displayContent;
                        const isAnimating = typingState?.isAnimating || isAnimatingRef.current[message.id] || false;

                        return (
                            <div key={message.id} className="flex flex-col">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center",
                                        message.role === "ai"
                                            ? (isBlackScheme ? "bg-black text-white" : "bg-blue-500 text-white")
                                            : (isBlackScheme ? "bg-gray-800 text-white" : "bg-gray-500 text-white")
                                    )}
                                    >{message.role === "ai" ? (
                                        <div>
                                            <img
                                                src={aiLogoSrc && aiName?.toLowerCase().includes('first move') ? aiLogoSrc : `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                                                alt={aiName}
                                                className="w-full h-full object-cover rounded-full"
                                            />
                                        </div>
                                    ) : userInitials ? (
                                        <span className="text-xs font-semibold leading-none">
                                            {userInitials}
                                        </span>
                                    ) : (
                                        <UserIcon className="h-4 w-4" />
                                    )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">
                                            {message.role === "ai" ? aiName : "You"}
                                        </span>
                                        <span className={cn(
                                            "text-xs",
                                            colorClasses.timestampText
                                        )}>
                                            {formatTime(message.timestamp)}
                                        </span>
                                    </div>
                                </div>
                                <div className={cn(
                                    "pl-10",
                                    colorClasses.messageText
                                )}>
                                    {/* Display the currently animated content */}
                                    <span className="whitespace-pre-wrap">
                                        {displayContent}
                                    </span>                                    {/* Blinking cursor for animating or incomplete messages */}
                                    {(isAnimating || (!message.isComplete && message.role === "ai")) && (
                                        <span className={cn(
                                            "inline-block ml-1 h-4 w-1.5 animate-typing-cursor",
                                            colorClasses.cursor
                                        )}></span>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/* AI Thinking Animation */}
                    {aiThinking && (
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2 mb-1">
                                <div className={cn(
                                    "w-8 h-8 rounded-full flex items-center justify-center",
                                    isBlackScheme ? "bg-black text-white" : "bg-blue-500 text-white"
                                )}>
                                    <div>
                                        <img
                                            src={aiLogoSrc && aiName?.toLowerCase().includes('first move') ? aiLogoSrc : `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                                            alt={aiName}
                                            className="w-full h-full object-cover rounded-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium">{aiName}</span>
                                    <span className={cn(
                                        "text-xs",
                                        colorClasses.timestampText
                                    )}>
                                        {formatTime(new Date())}
                                    </span>
                                </div>
                            </div>
                            <div className="pl-10">
                                <AiThinkingAnimation
                                    aiName={aiName}
                                    colorScheme={colorScheme}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </ScrollArea>

            {/* Scroll to bottom button - only shown when not at bottom */}
            {!autoScrollEnabledRef.current && (
                <button
                    onClick={() => {
                        autoScrollEnabledRef.current = true;
                        scrollToBottom(true);
                    }}
                    className="absolute bottom-16 right-4 bg-blue-500 text-white p-2 rounded-full shadow-md hover:bg-blue-600 transition-colors"
                    aria-label="Scroll to bottom"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>
            )}
        </div>
    );
}